
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { setUserOtaOverride } from '@/api/userOtaOverride'
import { PinListItem } from './PinList.vue'

/* eslint-disable camelcase */
// Interface fields mirror the snake_case shape the admin API returns so the
// parent can pass payload rows directly without re-mapping every field.
export interface DeviceLite {
  serial_number: string
  [k: string]: any
}
export interface OtaFileLite {
  id: number
  application_version_name?: string | null
  wireless_version_name?: string | null
  is_mandatory?: boolean
  [k: string]: any
}
/* eslint-enable camelcase */

/**
 * Reusable pin creation/update form.
 *
 * Modes are inferred from props rather than enumerated:
 *   - lockedDeviceSerial set  → device-scope (devices page, single cane fixed)
 *   - lockedOtaFile set       → ota-scope    (otafiles page, OTA fixed)
 *   - neither set             → user-scope   (cleanup page, user picks cane + OTA)
 *
 * Submit always calls setUserOtaOverride; the parent decides what to do next
 * via the 'saved' event.
 */
@Component({ name: 'PinForm' })
export default class PinForm extends Vue {
  // User the pin will belong to. Required — user picker happens outside the form.
  @Prop({ type: String, required: true }) userId!: string

  // Devices owned by `userId`. Used to populate the cane selector when device
  // is not locked. Order matters — first element is highlighted as "son bağlanılan".
  @Prop({ type: Array, default: () => [] }) userDevices!: DeviceLite[]

  // When set, the form pins to exactly this cane (devices page). Selector hides.
  @Prop({ type: String, default: null }) lockedDeviceSerial!: string | null

  // When set, the form pins exactly this OTA file (otafiles page). Selector hides.
  @Prop({ type: Object, default: null }) lockedOtaFile!: OtaFileLite | null

  // Catalog the OTA selector pulls from when OTA is not locked.
  @Prop({ type: Array, default: () => [] }) availableOtaFiles!: OtaFileLite[]

  // Whether to show the "tüm bastonlar için" toggle. False on devices page.
  @Prop({ type: Boolean, default: true }) allowUserScope!: boolean

  // The user's existing pins (already-normalized, same shape PinList renders).
  // When the selected device (or user-scope) already has a pin in this list,
  // we pre-fill the form and switch the button to "Pin'i güncelle". Single
  // source of truth — parent doesn't need to compute the match itself.
  @Prop({ type: Array, default: () => [] }) userPins!: PinListItem[]

  private selectedSerial: string | null = null
  private selectedOtaFileId: number | null = null
  private reason = ''
  private applyToAllUserDevices = false
  private saving = false

  created() {
    this.hydrate()
  }

  @Watch('userPins')
  onPinsChange() {
    this.hydrate()
  }

  // When the admin switches device/scope, pre-fill the form with that target's
  // existing pin (if any). Without these, changing the cane would leave the
  // OTA dropdown on the previous device's pin and confuse the operator.
  @Watch('selectedSerial')
  onSerialChange(newVal: string | null) {
    this.$emit('device-change', newVal)
    this.hydrate()
  }

  @Watch('applyToAllUserDevices')
  onScopeChange() {
    this.hydrate()
  }

  @Watch('lockedDeviceSerial')
  onLockedDeviceChange() {
    if (this.lockedDeviceSerial) {
      this.selectedSerial = this.lockedDeviceSerial
      this.applyToAllUserDevices = false
    }
  }

  @Watch('lockedOtaFile')
  onLockedOtaChange() {
    if (this.lockedOtaFile) {
      this.selectedOtaFileId = this.lockedOtaFile.id
    }
  }

  private hydrate() {
    // Default cane: locked > first device (selection persists if already chosen).
    if (!this.selectedSerial) {
      this.selectedSerial = this.lockedDeviceSerial ?? this.userDevices[0]?.serial_number ?? null
    }
    // Existing pin pre-fill — selectedSerial drives lookup, user-scope state
    // re-derives the user-scope existing pin row.
    const existing = this.existingPin
    if (existing) {
      this.selectedOtaFileId = existing.otaFileId
      this.reason = existing.reason ?? ''
    } else if (!this.lockedOtaFile) {
      // No matching pin and OTA isn't externally locked → leave fields empty.
      this.selectedOtaFileId = null
      this.reason = ''
    } else {
      this.selectedOtaFileId = this.lockedOtaFile.id
    }
  }

  get selectedDevice(): DeviceLite | undefined {
    return this.userDevices.find(d => d.serial_number === this.selectedSerial)
  }

  // Existing pin for the currently-selected scope. User-scope flag picks the
  // NULL-serial pin; otherwise the row whose deviceSerial matches the selected
  // serial. Computed so toggling cane / scope refreshes the "update" button
  // text and pre-fills without parent intervention.
  get existingPin(): PinListItem | null {
    if (!this.userPins?.length) return null
    if (this.applyToAllUserDevices) {
      return this.userPins.find(p => p.deviceSerial === null) || null
    }
    const target = this.lockedDeviceSerial || this.selectedSerial
    if (!target) return null
    return this.userPins.find(p => p.deviceSerial === target) || null
  }

  // Cane V2/V3 derivation from serial (matches backend classifyCaneFromSerial
  // and bulkGrantCleanup's classifyFromSerial). Serial format is fixed at
  // WW-OZ-XXXXN-XXXXXX where N=5 → V2, 6/7 → V3. Null when the serial
  // doesn't match → pinning is blocked.
  get effectiveCaneVersion(): 'V2' | 'V3' | null {
    if (this.applyToAllUserDevices) return null
    const s = this.selectedSerial
    if (!s) return null
    const m = s.match(/^WW-OZ-.{4}([567])-.{6}$/)
    if (!m) return null
    return m[1] === '5' ? 'V2' : 'V3'
  }

  // OTA list filtered to compatible major when a single cane is selected.
  // When applyToAllUserDevices is on, show every OTA (admin picks major
  // explicitly; backend validates against the user's devices on save).
  get filteredOtaFiles(): OtaFileLite[] {
    const list = this.availableOtaFiles || []
    if (this.applyToAllUserDevices) return list
    const v = this.effectiveCaneVersion
    if (!v) return list
    const required = v === 'V2' ? '2' : '3'
    return list.filter(f => {
      const ver = f.application_version_name || ''
      return ver.startsWith(`${required}.`) || ver === required
    })
  }

  // The OTA picker section renders only when there's actually a cane to pin to.
  // For user-scope and device-scope this gates on cane resolution; ota-scope
  // (locked OTA) always shows once a cane decision is made.
  get canShowOtaPicker(): boolean {
    if (this.applyToAllUserDevices) return true
    if (this.lockedDeviceSerial) return true
    return this.effectiveCaneVersion !== null
  }

  get canSubmit(): boolean {
    if (this.saving) return false
    if (!this.selectedOtaFileId) return false
    if (this.applyToAllUserDevices) return true
    if (this.lockedDeviceSerial) return true
    return !!this.selectedSerial && this.effectiveCaneVersion !== null
  }

  private otaLabel(f: OtaFileLite): string {
    const name = f.application_version_name || `id ${f.id}`
    return name + (f.is_mandatory ? ' (mandatory)' : '')
  }

  private onDeviceChange() {
    // Template @change still wires here for clarity; the heavy lifting (hydrate
    // + bubble) happens in the selectedSerial watcher above so programmatic
    // selectedSerial changes behave the same as user-driven ones.
  }

  private async save() {
    if (!this.canSubmit) return
    this.saving = true
    try {
      const deviceSerial = this.applyToAllUserDevices
        ? undefined
        : (this.lockedDeviceSerial ?? this.selectedSerial ?? undefined)
      const resp = await setUserOtaOverride(
        this.userId,
        this.selectedOtaFileId as number,
        this.reason || undefined,
        deviceSerial,
        this.applyToAllUserDevices
      )
      this.$message.success((this.existingPin ? this.$t('otaPin.pinUpdated') : this.$t('otaPin.pinApplied')) as string)
      this.$emit('saved', resp?.data?.data ?? null)
    } catch (err: any) {
      // Backend cane-compatibility guard returns 400 with a structured payload —
      // surface the human-readable hint to the operator instead of a generic
      // "request failed".
      const payload = err.response?.data
      const hint = payload?.data?.hint
      const code = payload?.status?.message
      const msg = hint
        ? this.$t('otaPin.applyFailed', { msg: hint }) as string
        : code
          ? this.$t('otaPin.applyFailedCode', { code }) as string
          : this.$t('otaPin.applyFailed', { msg: err.message || this.$t('otaPin.unknownError') }) as string
      this.$message({ message: msg, type: 'error', duration: 6000 })
    } finally {
      this.saving = false
    }
  }
}
